import { useEffect, useState } from 'react';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';
import { useApi } from '@backstage/core-plugin-api';
import { spassuCodeApiRef } from '../../api';
import type {
  Issue,
  IssueScope,
  IssueStateCategory,
  ProviderId,
} from '../../api/types';

const useStyles = makeStyles(theme => ({
  searchRow: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  badge: {
    marginLeft: theme.spacing(1),
  },
  empty: {
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
}));

const STATE_CATEGORY_COLORS: Record<
  IssueStateCategory,
  'default' | 'primary' | 'secondary'
> = {
  open: 'default',
  'in-progress': 'secondary', // Spassu orange in the brand palette
  done: 'primary',
};

export interface IssuePickerProps {
  /** SCM/Board provider id (e.g. 'azure-devops', 'github', 'jira'). */
  provider: ProviderId | 'jira';
  /** Scope passed to every search call. Required for Azure (project). */
  scope: IssueScope;
  /** Currently selected issue (controlled). */
  selected: Issue | null;
  onSelect: (issue: Issue | null) => void;
  /** Whether to render rich provider-specific extras (Azure description etc.). */
  richMode?: boolean;
  disabled?: boolean;
}

/**
 * Provider-agnostic issue picker. Replaces the legacy `WorkItemPicker` —
 * shape is the same to the user but searches via `/boards/:provider/issues`,
 * which means it works for Azure DevOps today and any future provider that
 * registers a `BoardProvider` (GitHub Issues in P1, GitLab Issues in P1+,
 * Jira in P2 — already supported by the contract).
 */
export function IssuePicker({
  provider,
  scope,
  selected,
  onSelect,
  richMode,
  disabled,
}: IssuePickerProps): JSX.Element {
  const classes = useStyles();
  const api = useApi(spassuCodeApiRef);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounce the query — every keystroke would otherwise hit the backend
  // and (for GitHub) burn through the 30 req/min unauthenticated search
  // rate-limit. 300ms is the same cadence used by the legacy WorkItemPicker.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .searchIssues(provider, {
        scope,
        q: debouncedQuery || undefined,
        pageSize: 25,
      })
      .then(result => {
        if (!cancelled) setResults(result.items);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, provider, scope.owner, scope.project, scope.repo, debouncedQuery]);

  return (
    <>
      {selected ? (
        <div className={classes.searchRow}>
          <Chip
            label={`${selected.type ? `${selected.type}: ` : ''}${selected.title}`}
            color={STATE_CATEGORY_COLORS[selected.stateCategory] ?? 'default'}
            onDelete={() => onSelect(null)}
            deleteIcon={<ClearIcon />}
            data-testid="issue-picker-selected"
          />
          {richMode && selected.raw != null && typeof selected.raw === 'object' && (
            <Chip
              label="Detalhes ricos disponíveis"
              variant="outlined"
              size="small"
              className={classes.badge}
            />
          )}
          <Button
            variant="outlined"
            size="small"
            onClick={() => setOpen(true)}
            disabled={disabled}
          >
            Trocar
          </Button>
        </div>
      ) : (
        <Button
          variant="outlined"
          onClick={() => setOpen(true)}
          disabled={disabled}
          data-testid="issue-picker-open"
        >
          Vincular Issue
        </Button>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Selecionar Issue</DialogTitle>
        <DialogContent>
          <div className={classes.searchRow}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por título ou ID..."
              value={query}
              onChange={ev => setQuery(ev.target.value)}
              inputProps={{ 'data-testid': 'issue-picker-search' }}
            />
            {loading && <CircularProgress size={20} />}
          </div>

          {error && (
            <Typography
              color="error"
              variant="body2"
              data-testid="issue-picker-error"
            >
              {error}
            </Typography>
          )}

          {!loading && !error && results.length === 0 && (
            <div className={classes.empty}>
              <Typography variant="body2">
                Nenhuma issue encontrada com esses filtros.
              </Typography>
            </div>
          )}

          <List dense data-testid="issue-picker-list">
            {results.map(item => (
              <ListItem
                key={`${item.providerId}-${item.externalId}`}
                button
                onClick={() => {
                  onSelect(item);
                  setOpen(false);
                }}
              >
                <ListItemText
                  primary={`${item.type ? `${item.type} ` : ''}#${item.externalId} — ${item.title}`}
                  secondary={
                    <>
                      <Chip
                        size="small"
                        label={item.state}
                        color={
                          STATE_CATEGORY_COLORS[item.stateCategory] ?? 'default'
                        }
                      />
                      {item.assignee && (
                        <span style={{ marginLeft: 8 }}>{item.assignee}</span>
                      )}
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="select"
                    onClick={() => {
                      onSelect(item);
                      setOpen(false);
                    }}
                  >
                    <CheckIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
